/* Presentation enhancements only. The existing fit quiz owns jacket / SKU decisions. */
(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const putText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };

  function init(root) {
    if (!root || root.dataset.afInitialized) return;
    root.dataset.afInitialized = 'true';
    const section = root.closest('.shopify-section');
    const abort = new AbortController();
    const on = (el, event, fn, options = {}) => el?.addEventListener(event, fn, { ...options, signal: abort.signal });
    let raf = 0;
    let knownQuiz = null;

    // "Check my fit": the fit selector scrolls to its first unanswered question and pulses it.
    function findFit(event) {
      event?.preventDefault();
      if (root.querySelector('.fur-quiz')) {
        document.dispatchEvent(new CustomEvent('arcticfur:check-fit', { detail: { formId: root.dataset.afForm } }));
        return;
      }
      const target = root.querySelector('variant-picker') || root.querySelector('product-form');
      if (!target) return;
      target.scrollIntoView({ behavior: reducedMotion() ? 'instant' : 'smooth', block: 'center' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }

    function setupQuiz() {
      const quiz = root.querySelector('.fur-quiz');
      if (!quiz || quiz === knownQuiz) return;
      knownQuiz = quiz;
      on(quiz, 'click', (event) => {
        if (!event.target.closest('.quiz-btn[data-next]')) return;
        queueMicrotask(() => {
          update();
          // Preserve natural tapping on touch; announce the new question for keyboard users.
          if (event.detail === 0) {
            const question = quiz.querySelector('.quiz-step:not(.hidden) .quiz_ques') || quiz.querySelector('.quiz-complete-message:not(.hidden)');
            question?.setAttribute('tabindex', '-1');
            question?.focus({ preventScroll: true });
          }
        });
      });
    }

    function update() {
      raf = 0;
      if (!root.isConnected) { observer.disconnect(); reviewObserver.disconnect(); abort.abort(); return; }
      setupQuiz();
      const quiz = root.querySelector('.fur-quiz');
      const complete = !quiz || quiz.dataset.afFitReady === 'true';
      root.dataset.afFitRequired = String(!complete);
      if (quiz) quiz.dataset.afComplete = String(complete);
      // Shopify's express button (Apple Pay and more) can't be disabled like a form button, so it's inert until the fit
      // is confirmed: no tap or key reaches it, and no order goes through without the jacket's details
      root.querySelectorAll('.product-info buy-buttons .shopify-payment-button').forEach((express) => { express.inert = !complete; });
      // "Your jacket: Wyndham Parka ✓ · change" under the price. The price block is re-rendered on
      // variant changes, so this runs again from the mutation observer.
      const chip = root.querySelector('[data-af-fit-chip]');
      if (chip) {
        const name = quiz && quiz.dataset.afFitCheck !== 'true' ? quiz.dataset.afFitName || '' : '';
        chip.hidden = !complete || !name;
        putText(chip.querySelector('[data-af-fit-chip-name]'), name);
      }
      const sticky = section.querySelector('[data-af-sticky]');
      if (sticky) {
        sticky.dataset.afNeedsFit = String(!complete);
        const form = document.getElementById(root.dataset.afForm);
        // The editorial page has a hero instead of the swipe gallery: the bar appears once either has scrolled away
        const gallery = root.querySelector('product-gallery') || root.querySelector('[data-af-hero]');
        const buyRect = form?.getBoundingClientRect();
        const galleryRect = gallery?.getBoundingClientRect();
        const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height')) || 80;
        const buyingVisible = buyRect && buyRect.bottom > headerHeight && buyRect.top < window.innerHeight - 70;
        const pastGallery = galleryRect ? galleryRect.bottom < headerHeight + 30 : root.getBoundingClientRect().top < -200;
        const footerRect = document.querySelector('.shopify-section--footer')?.getBoundingClientRect();
        const footerVisible = footerRect && footerRect.top < window.innerHeight;
        sticky.dataset.afVisible = String(pastGallery && !buyingVisible && !footerVisible);
      }
      updateGallery();
      updateRating();
    }

    function schedule() { if (!raf) raf = requestAnimationFrame(update); }

    function updateGallery() {
      const carousel = root.querySelector('.product-gallery__carousel');
      if (!carousel) return;
      const media = [...carousel.querySelectorAll('.product-gallery__media')].filter(node => !node.hidden);
      if (!media.length) return;
      const bounds = carousel.getBoundingClientRect();
      const centre = bounds.left + bounds.width / 2;
      let index = 0;
      let distance = Infinity;
      media.forEach((node, i) => {
        const rect = node.getBoundingClientRect();
        const next = Math.abs(rect.left + rect.width / 2 - centre);
        if (next < distance) { distance = next; index = i; }
      });
      putText(root.querySelector('[data-af-gallery-count]'), `${index + 1} / ${media.length}`);
    }

    function updateRating() {
      const link = root.querySelector('[data-af-review-link]');
      if (!link) return;
      const sources = [
        root.querySelector('[data-af-review-source] [data-average-rating][data-number-of-reviews]'),
        document.querySelector('.jdgm-rev-widg[data-average-rating][data-number-of-reviews]')
      ];
      for (const source of sources) {
        const rating = Number(source?.dataset.averageRating);
        const count = Number(source?.dataset.numberOfReviews);
        if (!(rating > 0 && rating <= 5 && Number.isInteger(count) && count > 0)) continue;
        const label = `${rating.toFixed(1)}/5 · ${count.toLocaleString()} ${count === 1 ? 'review' : 'reviews'}`;
        putText(link.querySelector('[data-af-rating-text]'), label);
        link.setAttribute('aria-label', `${label}. Read customer reviews`);
        link.querySelector('[data-af-rating-stars]').hidden = false;
        link.querySelector('[data-af-rating-fill]').style.width = `${rating * 20}%`;
        break;
      }
    }

    on(section, 'click', (event) => {
      if (event.target.closest('[data-af-find-fit]')) findFit(event);
      // The greyed-out buy buttons before the fit is confirmed: the fitting card comes into view and glows
      if (root.dataset.afFitRequired === 'true' && event.target.closest('.product-info buy-buttons')) findFit(event);
      if (event.target.closest('[data-af-change-jacket]')) {
        event.preventDefault();
        document.dispatchEvent(new CustomEvent('arcticfur:change-jacket', { detail: { formId: root.dataset.afForm } }));
      }
      if (event.target.closest('[data-af-review-link], .jdgm-prev-badge') && document.getElementById('af-customer-reviews')) {
        event.preventDefault();
        document.getElementById('af-customer-reviews').scrollIntoView({ behavior: reducedMotion() ? 'instant' : 'smooth', block: 'start' });
      }
    });
    on(document, 'click', (event) => { if (event.target.closest('[data-af-story-fit]')) findFit(event); });
    on(window, 'scroll', schedule, { passive: true });
    on(window, 'resize', schedule, { passive: true });
    on(root, 'scroll', schedule, { capture: true, passive: true });
    on(root, 'carousel:change', schedule);
    on(document, 'arcticfur:fit-change', schedule);
    on(document, 'variant:change', schedule);
    on(document, 'product:rerender', () => { queueMicrotask(schedule); });
    // Partial rerenders replace variant selectors, prices and the entire sticky bar.
    const observer = new MutationObserver(schedule);
    observer.observe(section, { childList: true, subtree: true });
    // Judge.me may load its genuine rating after the product section has rendered.
    const reviewObserver = new MutationObserver(updateRating);
    const reviewSection = document.getElementById('af-customer-reviews')?.closest('.shopify-section');
    if (reviewSection) reviewObserver.observe(reviewSection, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-average-rating', 'data-number-of-reviews'] });
    on(document, 'shopify:section:load', updateRating);
    on(document, 'shopify:section:unload', (event) => {
      if (event.target === section) { observer.disconnect(); reviewObserver.disconnect(); abort.abort(); cancelAnimationFrame(raf); }
    });
    update();
  }
  const mount = (scope = document) => scope.querySelectorAll('[data-af-product]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
